"use client";

import { Trophy, Building2, ClipboardList, Share2 } from "lucide-react";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { formatTime, getInitials } from "./helpers";
import { transformedImage, supabaseHost } from "@/app/_module/utils/imageTransform";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { SalesWinShareDialog } from "./SalesWinShareDialog";
import type { Message } from "./TeamChatService";


// Motion classes (.sales-win-pop / .sales-win-sheen) live in globals.css — defined
// once there instead of a per-card <style> injection, which would have duplicated the
// tag for every win in the channel. See globals.css for the keyframes + the
// prefers-reduced-motion gate.

export interface SalesWinCardProps {
  message: Message;
  /** True when the viewer IS the winner — the Share button renders only for them. */
  isOwn?: boolean;
}

export function SalesWinCard({ message, isOwn }: SalesWinCardProps) {
  const { branding, isWhiteLabeled } = useBranding();
  const meta = message.metadata as any;
  const premium = meta?.policyAmount ? (meta.policyAmount / 100) : null;
  const isClosedDeal = meta?.status === 'Sold & Approved';

  // The card is posted by the agent who made the sale (senderId is their user
  // id), so it is attributed to them rather than to the automation that
  // triggered it. metadata.agentName is the name captured at sale time and wins
  // over the sender record, which can drift if the account is renamed.
  const agentName = meta?.agentName || message.sender?.name || message.sender?.email || "Agent";

  // Every image goes through the transform endpoint. A raw profile picture on prod is
  // 2.2 MB of PNG; at these sizes it comes back as ~1 KB of WebP. Without this a busy
  // #sales-wins channel downloads tens of megabytes to render a handful of avatars.
  const gutterAvatar = transformedImage(message.sender?.profileImageUrl, { width: 80 });
  // Full-height photo panel (~128px wide, card-height tall) → 320 covers @2x.
  const heroAvatar = transformedImage(message.sender?.profileImageUrl, { width: 320, quality: 75 });
  // Resolved server-side, walking up to the nearest ancestor agency that has a logo —
  // so a sub-agency without its own branding still shows the parent's.
  // Logos use resize:"contain" — the default square "cover" crop turned wide agency
  // logos into a center slice (bit us with a 4267×3000 logo on the backdrop).
  const agencyLogo = transformedImage(meta?.agencyLogoUrl, { width: 400, quality: 60, resize: "contain" });
  const agencyChipLogo = transformedImage(meta?.agencyLogoUrl, { width: 48, resize: "contain" });

  // Backdrop fallback chain: agency's own mark → white-label brand's logo (an MRFG
  // agent in the MRFG portal gets MRFG behind the win) → the InsuraCentral favicon.
  // Something always brands the card, so the old shimmer fallback is gone entirely.
  // useBranding() only exposes a logoUrl when genuinely white-labeled, so a plain
  // agency profile picture can never leak in as the platform mark.
  const platformMark =
    isWhiteLabeled && branding?.logoUrl
      ? transformedImage(branding.logoUrl, { width: 400, quality: 60, resize: "contain" })
      : "/favicon.png";
  const backdropLogo = agencyLogo || platformMark;

  // The WINNER's white-label primary, resolved server-side up the same agency chain as
  // the logo. When present the card paints itself in the winning org's color (an MRFG
  // win is MRFG-gold for every viewer); otherwise it falls back to the viewer's portal
  // `primary` classes. Validated as 6-digit hex because it goes into inline styles.
  const brandColor = /^#[0-9a-fA-F]{6}$/.test(meta?.agencyBrandColor || "")
    ? (meta.agencyBrandColor as string)
    : null;

  // Share (winner only): opens the share dialog — preview of the rendered 1080×1080
  // (t=policywin, same pipeline as the leaderboard shares), send-to-phone QR for
  // Instagram, download, caption copy, and native share. The dialog owns the flow;
  // the card only composes the image URL + caption.
  const [shareOpen, setShareOpen] = useState(false);
  // Backdrop watermark shape, measured from the loaded image: wide wordmarks get the
  // off-edge bleed, square/round seals stay fully visible inside the card.
  const [wmIsWide, setWmIsWide] = useState(false);
  // Storage URLs travel as bare bucket paths ("profiles/agency-logos/x.jpg"), never as
  // full URLs — the share link is user-visible (hover, copy, QR), and it must not
  // expose the supabase host. The API rebuilds the pinned URL and applies its own
  // render transform server-side; anything that isn't our public-storage URL is
  // simply dropped (the card falls back to initials / no watermark).
  // Same-host URLs strip to bare paths (link privacy); assets on our OTHER
  // pinned project (dev DB sometimes holds prod-hosted photos) pass through
  // whole — rebuilding a foreign path against this env 404s to initials.
  const storagePath = (u?: string | null): string | null => {
    const m = (u || "").match(
      /^https:\/\/([a-z0-9-]+\.supabase\.co)\/storage\/v1\/(?:object|render\/image)\/public\/([^?]+)/
    );
    if (!m) return null;
    return m[1] === supabaseHost() ? decodeURIComponent(m[2]) : (u as string);
  };
  const shareImgPath = (() => {
    // v = template version. The route caches renders for an hour on identical URLs,
    // so bump this whenever the policywin TEMPLATE changes visually — otherwise
    // users keep seeing hour-old pixels after a deploy.
    const q = new URLSearchParams({ t: "policywin", v: "2", name: agentName });
    if (premium) q.set("prem", String(Math.round(premium)));
    if (meta?.carrier) q.set("carrier", meta.carrier);
    if (meta?.agencyName) q.set("agency", meta.agencyName);
    if (brandColor) q.set("color", brandColor);
    const photoPath = storagePath(message.sender?.profileImageUrl);
    if (photoPath) q.set("photo", photoPath);
    const alogoPath = storagePath(meta?.agencyLogoUrl);
    if (alogoPath) q.set("alogo", alogoPath);
    q.set("st", isClosedDeal ? "closed" : "pending");
    q.set("d", new Date(message.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }));
    return `/api/share-card?${q.toString()}`;
  })();
  const shareCaption = `🏆 Policy sold${premium ? ` — $${Math.round(premium).toLocaleString()}/yr` : ""}${meta?.carrier ? ` with ${meta.carrier}` : ""}. Another family protected. 🙏`;

  return (
    <div className="flex gap-3 px-4 py-2">
      {/* Agent avatar — same treatment as any other message in the channel, so
          a win reads as coming from a teammate. */}
      <Avatar className="mt-0.5 h-8 w-8 flex-shrink-0">
        <AvatarImage src={gutterAvatar} alt={agentName} loading="lazy" decoding="async" />
        <AvatarFallback className="bg-primary text-xs font-semibold text-white">
          {getInitials(agentName)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        {/* Agent name + time */}
        <div className="mb-1 flex items-baseline gap-2">
          <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{agentName}</span>
          <span className="text-[11px] text-zinc-400 dark:text-zinc-500">{formatTime(message.createdAt)}</span>
        </div>

        {/* Card.
            Design intent: three clean tiers — WHO (photo + name + agency identity),
            the NUMBER (premium, the only huge thing on the card), then a slim carrier
            footer. The previous version stacked a header, badges, glass boxes and a
            bottom accent and nothing dominated; here the eye lands on the face, then
            the figure. Sparkles removed — the agency mark IS the decoration now. */}
        <div className="sales-win-pop relative overflow-hidden rounded-2xl max-w-lg shadow-lg ring-1 ring-black/5">
          {/* Brand ground. `primary` is the CSS-var brand color — InsuraCentral blue by
              default, overridden per white-label by brandingContext (MRFG gets MRFG's
              color, etc.), so the card follows the portal's identity with zero
              per-brand logic here. The white→black diagonal overlay recreates the
              dimensionality the old amber gradient had, but is brand-agnostic — it
              reads as lighting on ANY base color. */}
          <div
            className={brandColor ? "absolute inset-0" : "absolute inset-0 bg-primary"}
            style={brandColor ? { backgroundColor: brandColor } : undefined}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/25" />

          {/* Brand mark as the backdrop — replaces the sweeping glow. The point is
              pride: the win visibly belongs to their agency (sub-agencies inherit the
              parent's mark server-side; no mark anywhere → white-label brand logo, else
              the InsuraCentral favicon). Luminosity blend + low opacity keeps it as
              texture behind the numbers; the scrim preserves contrast on the left where
              the name and premium sit. aria-hidden because the agency is named in the
              chip below. */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            {/* The oversized-bleed treatment suits wide agency wordmarks; the round
                InsuraCentral favicon fallback at 150% height just swallowed the whole
                card in a giant disc — the platform mark renders at a modest badge
                size instead. */}
            <img
              src={backdropLogo}
              alt=""
              loading="lazy"
              decoding="async"
              onLoad={(e) => {
                const t = e.currentTarget;
                setWmIsWide(t.naturalWidth > t.naturalHeight * 1.5);
              }}
              className={
                backdropLogo === "/favicon.png"
                  ? "absolute right-5 top-1/2 h-[44%] w-auto max-w-none -translate-y-1/2 -rotate-6 object-contain opacity-[0.14] mix-blend-luminosity"
                  : // multiply = smart white-knockout: white pixels contribute nothing
                    // (a white-background JPEG logo melts into the card color) while
                    // the darker artwork stays visible as a tint. Luminosity kept the
                    // white box as a pale panel. Wide wordmarks (MRFG) bleed off the
                    // right edge; square/round seals (BATCAVE) stay fully inside —
                    // one static offset couldn't suit both, so aspect decides.
                    `absolute top-1/2 h-[80%] w-auto max-w-none -translate-y-1/2 -rotate-6 object-contain opacity-30 mix-blend-multiply ${wmIsWide ? "-right-10" : "right-2"}`
              }
            />
            <div
              className={brandColor ? "absolute inset-0" : "absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/25 to-transparent"}
              style={brandColor ? { background: `linear-gradient(to right, ${brandColor}CC, ${brandColor}40, transparent)` } : undefined}
            />
          </div>

          <div className="relative">
            <div className="flex items-stretch">
              {/* Photo panel — the whole left side of the card, full height, so the
                  face is genuinely visible from across the channel ("we gotta see em
                  more"): a 72px thumbnail read as an avatar, this reads as a poster of
                  the person. Rounded corners come from the card's overflow-hidden.
                  Falls back to a big-initials panel when there's no photo. */}
              <div className="relative w-28 flex-shrink-0 sm:w-32">
                <Avatar className="absolute inset-0 h-full w-full rounded-none">
                  <AvatarImage
                    src={heroAvatar}
                    alt={agentName}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                  <AvatarFallback className="h-full w-full rounded-none bg-white/20 text-4xl font-black text-white backdrop-blur-sm">
                    {getInitials(agentName)}
                  </AvatarFallback>
                </Avatar>
                {/* Blends the photo's edge into the gold so the panel doesn't read as
                    a hard-cropped rectangle stuck onto the card. */}
                <div
                  className={brandColor ? "absolute inset-y-0 right-0 w-8" : "absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-primary/50 to-transparent"}
                  style={brandColor ? { background: `linear-gradient(to left, ${brandColor}80, transparent)` } : undefined}
                  aria-hidden="true"
                />
              </div>

              {/* Content column */}
              <div className="min-w-0 flex-1 px-4 py-4 sm:px-5">
                {/* One kicker, no status badge — "POLICY SOLD" + a "💰 Closed" chip said
                    the same thing twice. The kicker itself now carries the state:
                    closed reads POLICY SOLD, pending reads IN UNDERWRITING. */}
                <div className="flex items-start justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 pt-0.5 text-[11px] font-black uppercase tracking-[0.18em] text-white/90 drop-shadow-sm">
                    {isClosedDeal ? (
                      <><Trophy className="h-3.5 w-3.5" /> Policy Sold</>
                    ) : (
                      <><ClipboardList className="h-3.5 w-3.5" /> In Underwriting</>
                    )}
                  </span>
                  {/* Winner-only: everyone else just sees the clean card. */}
                  {isOwn && (
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      title="Share your win"
                      className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                    >
                      <Share2 className="h-3 w-3" />
                      Share
                    </button>
                  )}
                </div>

                <p className="mt-2 truncate text-2xl font-black leading-tight tracking-tight text-white drop-shadow-sm">
                  {agentName}
                </p>
                {meta?.agencyName && (
                  <span className="mt-1.5 inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/20 py-1 pl-1.5 pr-2.5 backdrop-blur-sm">
                    {agencyChipLogo ? (
                      <img
                        src={agencyChipLogo}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        decoding="async"
                        className="h-4 w-4 flex-shrink-0 rounded-full bg-white object-contain p-px"
                      />
                    ) : (
                      <Building2 className="h-3 w-3 flex-shrink-0 text-white/70" />
                    )}
                    <span className="truncate text-xs font-bold text-white">{meta.agencyName}</span>
                  </span>
                )}

                {/* The number — the one huge element on the card. */}
                {premium && (
                  <div className="pt-4">
                    {/* Same weight/contrast treatment as the POLICY SOLD kicker — at
                        white/60 this label disappeared on light brand colors like
                        MRFG's gold. White + drop-shadow survives any brand primary. */}
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/90 drop-shadow-sm">
                      Annual Premium
                    </p>
                    <div className="mt-0.5 flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-white/90 drop-shadow-md">$</span>
                      <span className="text-5xl font-black leading-none tracking-tight text-white drop-shadow-md">
                        {Math.round(premium).toLocaleString()}
                      </span>
                      <span className="ml-1 text-sm font-bold text-white/85 drop-shadow-sm">/yr</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Slim carrier footer — one quiet row instead of stacked glass boxes. */}
            {meta?.carrier && (
              <div className="flex items-center gap-2 border-t border-white/15 bg-black/10 px-5 py-2.5">
                <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-white/60" />
                <span className="truncate text-xs font-semibold text-white/90">{meta.carrier}</span>
              </div>
            )}
          </div>

          {/* Foil-card sheen: a narrow band of light sweeping the whole card (photo
              included — that's what sells the holo effect) about once every 6 seconds.
              Sits ABOVE the content on purpose; at white/20 it glints without ever
              obscuring. Taller than the card so the rotated band never clips short at
              the corners. Keyframes + reduced-motion gate in globals.css. */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="sales-win-sheen absolute -inset-y-8 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </div>
        </div>

        {/* Content text with @mention below the card */}
        {message.content && (
          <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-2 leading-relaxed">
            {(message.content || "").split(/(<@[^>]+>)/g).map((part, i) => {
              const mentionMatch = part.match(/^<@([^:]+):(.+)>$/);
              if (mentionMatch) {
                return (
                  <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-blue-500/20 dark:bg-blue-500/30 text-blue-600 dark:text-blue-300 font-semibold text-xs mx-0.5">
                    @{mentionMatch[2]}
                  </span>
                );
              }
              return part.split(/\*\*([^*]+)\*\*/g).map((seg, j) =>
                j % 2 === 1 ? <strong key={`${i}-${j}`}>{seg}</strong> : seg
              );
            })}
          </p>
        )}
      </div>

      {/* Mounted only for the winner — nobody else can open it, so nobody else pays
          for it. The preview <img> inside only fetches once the dialog opens. */}
      {isOwn && (
        <SalesWinShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          imgPath={shareImgPath}
          caption={shareCaption}
        />
      )}
    </div>
  );
}
